import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import { AppContext } from "AppContainer";
import Button from "components/Button";
import Input from "components/Input";
import { axiosInstance } from "http";
import queryString from "query-string";
import { useContext, useEffect, useState } from "react";
import { GoPasskeyFill } from "react-icons/go";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GetAssertReq, publicKeyCredentialToJSON } from "./auth";
import { LOGIN, VERIFY_MFA_CODE } from "./graphql";

const LoginForm = () => {
  const { user, setUser } = useContext(AppContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userId, setUserId] = useState(null);
  const [enableMfa, setEnableMfa] = useState(false);
  const [showLoginInWithPasskeys, setShowLoginInWithPasskeys] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const [secureLogin, secureLoginRes] = useMutation(LOGIN, {
    variables: { email, password, device: localStorage.getItem("device") },
    onCompleted: (data) => {
      if (data.secureLogin.loginDirectly) {
        const redirectTo = queryString.parse(location.search).redirectTo;
        if (redirectTo) {
          window.open(redirectTo, "_self");
        } else {
          setUser(data.secureLogin.me);
          toast.success("You are logged in.");
        }
      } else if (data.secureLogin.enableMfa) {
        setUserId(data.secureLogin.userId);
        setEnableMfa(data.secureLogin.enableMfa);
      }
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });

  async function signInWithPassskeys() {
    setPasskeyLoading(true);
    try {
      let res = await axiosInstance.get("passkeys/auth/begin");
      const abortController = new AbortController();
      let options = GetAssertReq(res.data);
      options.signal = abortController.signal;
      const credential = await navigator.credentials.get(options);
      const passkeys = JSON.stringify(publicKeyCredentialToJSON(credential));
      let data = new FormData();
      data.append("passkeys", passkeys);
      data.append("username", "");
      data.append("password", "");
      res = await axiosInstance.post("login-with-passkeys", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        const redirectTo = queryString.parse(location.search).redirectTo || "/";
        window.open(redirectTo, "_self");
      }
    } catch (error) {
      setPasskeyLoading(false);
      if (error.name === "NotAllowedError") return;
      toast.error(parseError(error));
    }
  }

  async function checkIfPasskeyIsAvailable() {
    if (
      window.PublicKeyCredential &&
      window.PublicKeyCredential.isConditionalMediationAvailable
    ) {
      const isCMA =
        await window.PublicKeyCredential.isConditionalMediationAvailable();
      if (isCMA) setShowLoginInWithPasskeys(true);
    }
  }

  useEffect(() => {
    checkIfPasskeyIsAvailable();
  }, []);

  useEffect(() => {
    if (user) {
      let redirectTo = queryString.parse(location.search).redirectTo || "/me";
      navigate(redirectTo);
    }
  }, [user, location.search, navigate]);

  const [verifyMfaCode, verifyMfaCodeRes] = useMutation(VERIFY_MFA_CODE, {
    variables: { userId, code },
    onCompleted: (data) => {
      localStorage.setItem("device", data.verifyMfaCode.device);
      const redirectTo = queryString.parse(location.search).redirectTo;
      if (redirectTo) {
        window.open(redirectTo, "_self");
      } else {
        setUser(data.verifyMfaCode.user);
        toast.success("You are logged in.");
      }
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });

  function submit(e) {
    e.preventDefault();
    if (enableMfa) {
      verifyMfaCode();
    } else {
      secureLogin();
    }
  }

  return (
    <div>
      <h4 className="text-center mt-4">Sign In 登陆</h4>
      <form className="mt-6" onSubmit={submit}>
        <div>
          <Input
            className="text-center w-full"
            required
            type="email"
            autoComplete="email webauthn"
            value={email}
            placeholder="Email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="mt-4">
          <Input
            className="text-center w-full"
            required
            type="password"
            value={password}
            placeholder="Password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {enableMfa ? (
          <div>
            <div className="mt-4">
              <Input
                className="text-center w-full"
                value={code}
                autoFocus={true}
                placeholder="Authentication Code"
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <div className="mt-4 flex justify-center">
              <Button loading={verifyMfaCodeRes.loading} type="submit" primary>
                {verifyMfaCodeRes.loading ? "Signing in..." : "Sign In"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-8 flex justify-center">
            <Button loading={secureLoginRes.loading} type="submit" primary>
              {secureLoginRes.loading
                ? "Signing in..."
                : "Sign in with Password / 使用密码登录"}
            </Button>
          </div>
        )}
      </form>

      <div className="text-sm opacity-70 text-center mt-4">
        <Link className=" text-sky-600 font-normal" to="reset-password">
          Reset Password / 重置密码
        </Link>
      </div>

      {showLoginInWithPasskeys ? (
        <div className="flex justify-center border-t dark:border-gray-700 border-opacity-80 my-6 pt-8">
          <Button
            className="text-sm"
            onClick={signInWithPassskeys}
            loading={passkeyLoading}
            icon={<GoPasskeyFill size={18} />}
          >
            Sign In with Passkeys / 使用 Passkeys 登录
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default LoginForm;
